import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dimmer, Loader, Segment } from "semantic-ui-react";
import moment from "moment";
import { GridPager } from "../../common/pagination/GridPager";
import StatusStats from "../../common/ui/status-stats/StatusStats";
import CommonPageHeader from "../../common/ui/page-header/CommonPageHeader";
import DataTable from "../../common/ui/data-table/DataTable";
import ExcelExportButton from "../../common/ui/excel-export-button/ExcelExportButton";
import SearchInput from "../../common/ui/search-input/SearchInput";
import SelectFilter from "../../common/ui/select-filter/SelectFilter";
import RowsPerPageSelect from "../../common/ui/rows-per-page-select/RowsPerPageSelect";
import "rc-pagination/assets/index.css";
import "./Documents.css";
import { axiosBasic } from "../../../api/axios";
import { config } from "../../../api/endPoints";
import { Common } from "../../../utils";

/** Backend CompanyDocumentAcceptanceStatus : short — order matches C# enum */
const ACCEPTANCE_STATUS = {
  Pending: 0,
  Accepted: 1,
  Rejected: 2,
};

const categoryLabels = {
  InternalPolicy: "Internal Policy",
  Regulatory: "Regulatory",
  Training: "Training",
  TermsAndConditions: "Terms & Conditions",
  EmployeeHandbook: "Employee Handbook",
  Safety: "Safety",
  Other: "Other",
};

/** Matches backend CompanyDocumentCategory : short order */
const CATEGORY_INDEX_TO_KEY = [
  "InternalPolicy",
  "Regulatory",
  "Training",
  "TermsAndConditions",
  "EmployeeHandbook",
  "Safety",
  "Other",
];

const getCategoryLabel = (category) => {
  if (category === null || category === undefined) return "—";
  if (typeof category === "string") return categoryLabels[category] || category;
  if (typeof category === "number") {
    const key = CATEGORY_INDEX_TO_KEY[category];
    return key ? categoryLabels[key] || key : String(category);
  }
  return String(category);
};

const clampPercent = (n) => {
  const x = Number(n);
  if (Number.isNaN(x)) return 0;
  return Math.min(100, Math.max(0, Math.round(x)));
};

/** Stacked bar flex weights from summary counts (omit zero segments). */
const complianceStatusSegments = (summary) => {
  if (!summary) return { total: 0, segments: [] };
  const accepted = Math.max(0, Number(summary.acceptedCount) || 0);
  const pending = Math.max(0, Number(summary.pendingCount) || 0);
  const rejected = Math.max(0, Number(summary.rejectedCount) || 0);
  const total = accepted + pending + rejected;
  const segments = [];
  if (accepted > 0) segments.push({ key: "accepted", flex: accepted, className: "documents-compliance-summary-seg--accepted" });
  if (pending > 0) segments.push({ key: "pending", flex: pending, className: "documents-compliance-summary-seg--pending" });
  if (rejected > 0) segments.push({ key: "rejected", flex: rejected, className: "documents-compliance-summary-seg--rejected" });
  return { total, segments };
};

/**
 * Admin compliance summary — supports legacy aggregate or CompanyDocumentPortalAcceptanceStats-shaped payload.
 * When only acceptancePercent is present (no overallCompliancePercent), percent = accepted share among
 * completed responses (pending excluded), per API contract.
 */
const normalizeSummary = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const acceptedCount = Number(raw.acceptedCount ?? raw.AcceptedCount) || 0;
  const pendingCount = Number(raw.pendingCount ?? raw.PendingCount) || 0;
  const rejectedCount = Number(raw.rejectedCount ?? raw.RejectedCount) || 0;
  const totalRequiringAcceptance =
    Number(raw.totalRequiringAcceptance ?? raw.TotalRequiringAcceptance) || 0;
  const documentsNotRequiringAcceptance =
    Number(raw.documentsNotRequiringAcceptance ?? raw.DocumentsNotRequiringAcceptance) || 0;

  const legacyOverall = raw.overallCompliancePercent ?? raw.OverallCompliancePercent;
  const hasLegacyOverall =
    legacyOverall !== undefined && legacyOverall !== null && legacyOverall !== "";
  const portalPercent = raw.acceptancePercent ?? raw.AcceptancePercent;

  const overallCompliancePercent = clampPercent(
    hasLegacyOverall ? legacyOverall : portalPercent ?? 0
  );

  const legacyTotal = raw.totalEntries ?? raw.TotalEntries;
  const totalEntries =
    typeof legacyTotal === "number" && !Number.isNaN(Number(legacyTotal))
      ? Number(legacyTotal)
      : Math.max(0, acceptedCount + pendingCount + rejectedCount);

  return {
    overallCompliancePercent,
    totalEntries,
    acceptedCount,
    pendingCount,
    rejectedCount,
    totalRequiringAcceptance,
    documentsNotRequiringAcceptance,
    usesRespondedOnlyPercent: !hasLegacyOverall,
  };
};

const coerceStatus = (v) => {
  if (v === null || v === undefined) return ACCEPTANCE_STATUS.Pending;
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  const s = String(v).toLowerCase();
  if (s === "pending" || s === "0") return ACCEPTANCE_STATUS.Pending;
  if (s === "accepted" || s === "1") return ACCEPTANCE_STATUS.Accepted;
  if (s === "rejected" || s === "2") return ACCEPTANCE_STATUS.Rejected;
  return ACCEPTANCE_STATUS.Pending;
};

const normalizeEntry = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const responded = raw.respondedDate ?? raw.RespondedDate ?? null;
  return {
    acknowledgementId: String(raw.acknowledgementId ?? raw.AcknowledgementId ?? ""),
    userId: String(raw.userId ?? raw.UserId ?? ""),
    userName: raw.userName ?? raw.UserName ?? "—",
    email: raw.email ?? raw.Email ?? "",
    imageUrl: raw.imageUrl ?? raw.ImageUrl ?? "",
    designation: raw.designation ?? raw.Designation ?? "",
    departmentId: raw.departmentId ?? raw.DepartmentId ?? null,
    departmentName: raw.departmentName ?? raw.DepartmentName ?? "—",
    officeId: raw.officeId ?? raw.OfficeId ?? null,
    officeName: raw.officeName ?? raw.OfficeName ?? "—",
    documentId: String(raw.documentId ?? raw.DocumentId ?? ""),
    documentTitle: raw.documentTitle ?? raw.DocumentTitle ?? "—",
    documentCategory: raw.documentCategory ?? raw.DocumentCategory,
    status: coerceStatus(raw.status ?? raw.Status),
    respondedDate: responded,
    responseComment: raw.responseComment ?? raw.ResponseComment ?? "",
  };
};

const statusLabel = (status) => {
  if (status === ACCEPTANCE_STATUS.Accepted) return "Accepted";
  if (status === ACCEPTANCE_STATUS.Rejected) return "Rejected";
  return "Pending";
};

const statusChipClass = (status) => {
  if (status === ACCEPTANCE_STATUS.Accepted) return "chip-success";
  if (status === ACCEPTANCE_STATUS.Rejected) return "chip-danger";
  return "chip-pending";
};

const formatRespondedDate = (iso) => {
  if (!iso) return "—";
  const m = moment(iso);
  return m.isValid() ? m.format("MMM D, YYYY h:mm A") : "—";
};

/** Query + client sort keys — align with API when backend supports SortBy / SortDescending */
const COMPLIANCE_SORT_KEYS = {
  userName: "UserName",
  documentTitle: "DocumentTitle",
  documentCategory: "DocumentCategory",
  departmentName: "DepartmentName",
  officeName: "OfficeName",
  status: "Status",
  respondedDate: "RespondedDate",
};

const hasRecordedResponse = (status) =>
  status === ACCEPTANCE_STATUS.Accepted || status === ACCEPTANCE_STATUS.Rejected;

const parseDownloadFilename = (contentDisposition) => {
  if (!contentDisposition || typeof contentDisposition !== "string") return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1].trim().replace(/^"|"$/g, ""));
    } catch {
      return utf8[1].trim();
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(contentDisposition);
  if (quoted) return quoted[1];
  const plain = /filename=([^;]+)/i.exec(contentDisposition);
  if (plain) return plain[1].trim().replace(/^"|"$/g, "");
  return null;
};

const ComplianceTracking = () => {
  const [summary, setSummary] = useState(null);
  const [entries, setEntries] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [departments, setDepartments] = useState([]);
  const [offices, setOffices] = useState([]);

  const [filterDepartmentId, setFilterDepartmentId] = useState(null);
  const [filterOfficeId, setFilterOfficeId] = useState(null);
  const [filterStatus, setFilterStatus] = useState(null);

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const prevDebouncedSearchRef = useRef(debouncedSearch);

  const [sortKey, setSortKey] = useState("userName");
  const [sortDir, setSortDir] = useState("asc");

  /** Row opened in “View response” modal (accepted / rejected + optional ResponseComment) */
  const [responseDetailRow, setResponseDetailRow] = useState(null);
  /** null | "excel" | "csv" | "pdf" */
  const [exportLoading, setExportLoading] = useState(null);

  const fetchCompliance = useCallback(
    async (overrides = {}) => {
      const usePage = overrides.page !== undefined ? overrides.page : page;
      setLoading(true);
      try {
        const { CompanyDocuments } = config.REST_API;
        const searchTrim = debouncedSearch.trim();
        const hasSearch = searchTrim.length > 0;
        const params = { page: usePage, pageSize };
        if (filterDepartmentId != null) params.departmentId = filterDepartmentId;
        if (filterOfficeId != null) params.officeId = filterOfficeId;
        if (filterStatus !== null && filterStatus !== "") params.status = filterStatus;
        if (hasSearch) {
          params.search = searchTrim;
        } else {
          const apiSort = COMPLIANCE_SORT_KEYS[sortKey];
          if (apiSort) {
            params.sortBy = apiSort;
            params.sortDescending = sortDir === "desc";
          }
        }

        const endpoint = hasSearch
          ? CompanyDocuments.SearchComplianceTracking
          : CompanyDocuments.GetComplianceTracking;

        const res = await axiosBasic.get(endpoint, { params });
      const body = res.data || {};
      const { success, data, message } = body;
      if (!success) {
        Common.showErrorMessage(message || "Could not load compliance data.");
        setSummary(null);
        setEntries([]);
        setTotalCount(0);
        return;
      }

      const payload = data ?? body.data ?? body;
      const summaryRaw = payload?.summary ?? payload?.Summary;
      const entriesRaw = payload?.entries ?? payload?.Entries ?? [];
      const total =
        payload?.totalCount ??
        payload?.TotalCount ??
        (Array.isArray(entriesRaw) ? entriesRaw.length : 0);

      setSummary(normalizeSummary(summaryRaw));
      setEntries(Array.isArray(entriesRaw) ? entriesRaw.map(normalizeEntry).filter(Boolean) : []);
      setTotalCount(typeof total === "number" && !Number.isNaN(total) ? total : 0);
    } catch (err) {
      Common.showErrorMessage(err?.response?.data?.message || err?.message || "Could not load compliance data.");
      setSummary(null);
      setEntries([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
    },
    [page, pageSize, filterDepartmentId, filterOfficeId, filterStatus, sortKey, sortDir, debouncedSearch]
  );

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 380);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const { Department, Office } = config.REST_API;
        const [depRes, offRes] = await Promise.all([
          axiosBasic.get(Department.Get),
          axiosBasic.get(Office.GetCompanyOffices),
        ]);
        const depBody = depRes.data || {};
        if (depBody.success && Array.isArray(depBody.data)) {
          setDepartments(
            depBody.data
              .map((d) => ({
                id: d.id,
                name: d.name,
              }))
              .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { sensitivity: "base" }))
          );
        } else {
          setDepartments([]);
        }
        const offBody = offRes.data || {};
        if (offBody.success && Array.isArray(offBody.data)) {
          setOffices(
            offBody.data.map((o) => ({
              id: o.id,
              label: o.countryName ? `${o.name} (${o.countryName})` : o.name,
            }))
          );
        } else {
          setOffices([]);
        }
      } catch {
        setDepartments([]);
        setOffices([]);
      }
    };
    loadLookups();
  }, []);

  useEffect(() => {
    const searchChanged = prevDebouncedSearchRef.current !== debouncedSearch;
    prevDebouncedSearchRef.current = debouncedSearch;
    if (searchChanged) {
      setPage(1);
    }
    fetchCompliance({ page: searchChanged ? 1 : undefined });
  }, [fetchCompliance, debouncedSearch]);

  useEffect(() => {
    if (!responseDetailRow) return;
    const onKey = (e) => {
      if (e.key === "Escape") setResponseDetailRow(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [responseDetailRow]);

  const handlePagerChange = (current, nextPageSize) => {
    const resolvedSize =
      nextPageSize != null && nextPageSize !== "" ? Number(nextPageSize) : pageSize;
    if (resolvedSize !== pageSize) {
      setPageSize(resolvedSize);
      setPage(1);
    } else {
      setPage(current);
    }
  };

  const buildComplianceExportParams = useCallback(() => {
    const params = {};
    if (filterDepartmentId != null) params.departmentId = filterDepartmentId;
    if (filterOfficeId != null) params.officeId = filterOfficeId;
    if (filterStatus !== null && filterStatus !== "") params.status = filterStatus;
    const q = debouncedSearch.trim();
    if (q) params.search = q;
    return params;
  }, [filterDepartmentId, filterOfficeId, filterStatus, debouncedSearch]);

  const handleComplianceExport = async (format) => {
    if (exportLoading) return;
    const { CompanyDocuments } = config.REST_API;
    const exportConfig = {
      excel: {
        url: CompanyDocuments.ExportComplianceTracking,
        mime: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        ext: "xlsx",
        success: "Excel report downloaded.",
      },
      csv: {
        url: CompanyDocuments.ExportComplianceTrackingCsv,
        mime: "text/csv;charset=utf-8",
        ext: "csv",
        success: "CSV report downloaded.",
      },
      pdf: {
        url: CompanyDocuments.ExportComplianceTrackingPdf,
        mime: "application/pdf",
        ext: "pdf",
        success: "PDF report downloaded.",
      },
    }[format];
    if (!exportConfig) return;

    setExportLoading(format);
    try {
      const res = await axiosBasic.get(exportConfig.url, {
        params: buildComplianceExportParams(),
        responseType: "blob",
      });

      let raw = res.data;
      if (raw instanceof Blob && raw.type && raw.type.includes("application/json")) {
        const text = await raw.text();
        try {
          const parsed = JSON.parse(text);
          Common.showErrorMessage(parsed?.message || "Could not export compliance report.");
        } catch {
          Common.showErrorMessage(text || "Could not export compliance report.");
        }
        return;
      }

      const blob = new Blob([raw], { type: exportConfig.mime });

      const headerName = parseDownloadFilename(res.headers["content-disposition"]);
      const filename =
        headerName || `compliance-tracking-${moment().format("YYYY-MM-DD-HHmm")}.${exportConfig.ext}`;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      Common.showSuccessMessage(exportConfig.success);
    } catch (err) {
      let msg = err?.message || "Could not export compliance report.";
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          if (parsed?.message) msg = parsed.message;
        } catch {
          /* keep msg */
        }
      } else if (err?.response?.data?.message) {
        msg = err.response.data.message;
      }
      Common.showErrorMessage(msg);
    } finally {
      setExportLoading(null);
    }
  };

  const handleSortColumn = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
    setPage(1);
  };

  const sortedEntries = useMemo(() => {
    const list = [...entries];
    const mult = sortDir === "asc" ? 1 : -1;
    const key = sortKey;
    list.sort((a, b) => {
      let va;
      let vb;
      switch (key) {
        case "userName":
          va = String(a.userName || "").toLowerCase();
          vb = String(b.userName || "").toLowerCase();
          break;
        case "documentTitle":
          va = String(a.documentTitle || "").toLowerCase();
          vb = String(b.documentTitle || "").toLowerCase();
          break;
        case "documentCategory":
          va = getCategoryLabel(a.documentCategory).toLowerCase();
          vb = getCategoryLabel(b.documentCategory).toLowerCase();
          break;
        case "departmentName":
          va = String(a.departmentName || "").toLowerCase();
          vb = String(b.departmentName || "").toLowerCase();
          break;
        case "officeName":
          va = String(a.officeName || "").toLowerCase();
          vb = String(b.officeName || "").toLowerCase();
          break;
        case "status":
          va = a.status;
          vb = b.status;
          break;
        case "respondedDate": {
          const ts = (row) => {
            if (row.status === ACCEPTANCE_STATUS.Pending || !row.respondedDate) return null;
            const m = moment(row.respondedDate);
            return m.isValid() ? m.valueOf() : null;
          };
          const ta = ts(a);
          const tb = ts(b);
          if (ta === null && tb === null) return 0;
          if (ta === null) return 1 * mult;
          if (tb === null) return -1 * mult;
          va = ta;
          vb = tb;
          break;
        }
        default:
          return 0;
      }
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      return 0;
    });
    return list;
  }, [entries, sortKey, sortDir]);

  const tableColumns = useMemo(
    () => [
      { key: "userName", label: "Employee", sortable: true },
      { key: "documentTitle", label: "Document", sortable: true },
      { key: "documentCategory", label: "Category", sortable: true },
      { key: "departmentName", label: "Department", sortable: true },
      { key: "officeName", label: "Office", sortable: true },
      { key: "status", label: "Status", sortable: true },
      { key: "respondedDate", label: "Response date", sortable: true },
      { key: "response", label: "Response", sortable: false, headerClassName: "documents-table-th-actions" },
    ],
    []
  );

  const complianceBreakdownBar = useMemo(() => {
    if (!summary) return null;
    const { total: segTotal, segments } = complianceStatusSegments(summary);
    const a11yBreakdown = `${summary.acceptedCount ?? 0} accepted, ${summary.pendingCount ?? 0} pending, ${summary.rejectedCount ?? 0} rejected${
      segTotal > 0 ? ` of ${segTotal}` : ""
    }`;
    return (
      <div
        className="documents-compliance-summary-track documents-compliance-summary-track--wide"
        role="group"
        aria-label={`${a11yBreakdown}. Headline rate ${summary.overallCompliancePercent}% accepted.`}
      >
        {segTotal > 0 ? (
          <div className="documents-compliance-summary-segments" aria-hidden>
            {segments.map((seg) => (
              <div
                key={seg.key}
                className={`documents-compliance-summary-seg ${seg.className}`}
                style={{ flex: seg.flex }}
              />
            ))}
          </div>
        ) : (
          <div className="documents-compliance-summary-segments documents-compliance-summary-segments--empty" aria-hidden />
        )}
      </div>
    );
  }, [summary]);

  return (
    <div className="documents-page container-fluid px-3 px-md-4 py-4">
      <CommonPageHeader
        icon={<i className="bi bi-clipboard2-data" />}
        title="Compliance Tracking"
        description="Track acknowledgement status across departments and keep an auditable trail of who accepted, rejected, or still needs follow-up. Only active documents that require acceptance are included."
      />

      <div className="documents-compliance-summary-row" aria-label="Compliance summary">
        <div className="documents-compliance-overall">
          <div className="documents-compliance-overall-label">
            {summary?.usesRespondedOnlyPercent ? "Acceptance rate (completed)" : "Overall compliance"}
          </div>
          {summary ? (
            <>
              <div className="documents-compliance-overall-value">{summary.overallCompliancePercent}%</div>
              {complianceBreakdownBar}
              <p className="documents-compliance-overall-caption">
                {summary.usesRespondedOnlyPercent ? (
                  <>
                    Among acknowledgements with a response, the share that were accepted; pending is excluded from this
                    rate.{typeof totalCount === "number" ? ` ${totalCount} row(s) match your filters.` : ""}
                  </>
                ) : (
                  <>Accepted share of entries matching your filters ({summary.totalEntries} total).</>
                )}
                {summary.documentsNotRequiringAcceptance > 0 ? (
                  <>
                    {" "}
                    Reference documents (no acknowledgement required) in scope:{" "}
                    {summary.documentsNotRequiringAcceptance}.
                  </>
                ) : null}
              </p>
            </>
          ) : (
            <p className="documents-compliance-overall-caption">—</p>
          )}
        </div>
        <StatusStats
          acceptedCount={summary ? summary.acceptedCount : "—"}
          pendingCount={summary ? summary.pendingCount : "—"}
          rejectedCount={summary ? summary.rejectedCount : "—"}
          pendingTitle={
            summary?.usesRespondedOnlyPercent
              ? "Still awaiting accept or reject (same basis as the action queue)"
              : undefined
          }
        />
      </div>

      <div className="documents-filter-bar documents-filter-bar--compliance-tracking">
        <div className="documents-filter-bar-left documents-compliance-filters">
          <SearchInput
            value={searchInput}
            onChange={setSearchInput}
            onClear={() => setSearchInput("")}
            placeholder="Search people, documents, comments..."
            ariaLabel="Search compliance records"
            wrapperClassName="documents-filter-search-wrap"
            inputClassName="documents-input documents-filter-search-input"
            iconClassName="bi bi-search documents-filter-search-icon"
            clearButtonClassName="documents-filter-search-clear"
          />
          <SelectFilter
            value={filterDepartmentId != null ? String(filterDepartmentId) : ""}
            onChange={(v) => {
              setFilterDepartmentId(v === "" ? null : Number(v));
              setPage(1);
            }}
            options={[{ value: "", label: "All departments" }, ...departments.map((d) => ({ value: String(d.id), label: d.name }))]}
            ariaLabel="Filter by department"
            wrapperClassName="documents-filter-office"
            iconClassName="bi bi-diagram-3 documents-filter-office-icon"
            selectClassName="documents-input documents-filter-select"
          />
          <SelectFilter
            value={filterOfficeId != null ? String(filterOfficeId) : ""}
            onChange={(v) => {
              setFilterOfficeId(v === "" ? null : Number(v));
              setPage(1);
            }}
            options={[{ value: "", label: "All offices" }, ...offices.map((o) => ({ value: String(o.id), label: o.label }))]}
            ariaLabel="Filter by office"
            wrapperClassName="documents-filter-office"
            iconClassName="bi bi-building documents-filter-office-icon"
            selectClassName="documents-input documents-filter-select"
          />
          <SelectFilter
            value={filterStatus === null ? "" : String(filterStatus)}
            onChange={(v) => {
              if (v === "") setFilterStatus(null);
              else setFilterStatus(Number(v));
              setPage(1);
            }}
            options={[
              { value: "", label: "All statuses" },
              { value: String(ACCEPTANCE_STATUS.Pending), label: "Pending" },
              { value: String(ACCEPTANCE_STATUS.Accepted), label: "Accepted" },
              { value: String(ACCEPTANCE_STATUS.Rejected), label: "Rejected" },
            ]}
            ariaLabel="Filter by acknowledgement status"
            wrapperClassName="documents-filter-office"
            iconClassName="bi bi-funnel documents-filter-office-icon"
            selectClassName="documents-input documents-filter-select"
          />
        </div>
        <div className="documents-filter-bar-right">
          <div className="documents-compliance-export-wrap" role="group" aria-label="Export compliance report">
            <ExcelExportButton
              className="documents-compliance-export-btn"
              onClick={() => handleComplianceExport("excel")}
              disabled={Boolean(exportLoading)}
              loading={exportLoading === "excel"}
              title="Download Excel workbook (summary and by employee)"
              label="Excel"
              loadingLabel="Exporting…"
            />
            <button
              type="button"
              className="excel-export-button documents-compliance-export-btn documents-compliance-export-btn--csv"
              onClick={() => handleComplianceExport("csv")}
              disabled={Boolean(exportLoading)}
              title="Download UTF-8 CSV (with BOM), same filters as Excel"
            >
              {exportLoading === "csv" ? (
                <span className="documents-compliance-export-spinner" role="status" aria-live="polite">
                  <span className="spinner-border spinner-border-sm" aria-hidden />
                  <span>Exporting…</span>
                </span>
              ) : (
                <>
                  <i className="bi bi-filetype-csv" aria-hidden />
                  <span>CSV</span>
                </>
              )}
            </button>
            <button
              type="button"
              className="excel-export-button documents-compliance-export-btn documents-compliance-export-btn--pdf"
              onClick={() => handleComplianceExport("pdf")}
              disabled={Boolean(exportLoading)}
              title="Download landscape PDF table, same filters as Excel"
            >
              {exportLoading === "pdf" ? (
                <span className="documents-compliance-export-spinner" role="status" aria-live="polite">
                  <span className="spinner-border spinner-border-sm" aria-hidden />
                  <span>Exporting…</span>
                </span>
              ) : (
                <>
                  <i className="bi bi-file-earmark-pdf" aria-hidden />
                  <span>PDF</span>
                </>
              )}
            </button>
          </div>
          <RowsPerPageSelect
            value={pageSize}
            onChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
            options={[10, 20, 50, 100]}
            label="Rows per page"
            ariaLabel="Number of records per page"
            wrapperClassName="documents-filter-page-size"
            labelClassName="documents-filter-page-size-label"
            selectClassName="documents-input documents-filter-select documents-filter-page-size-ddl"
          />
        </div>
      </div>

      <div className="documents-table-wrap">
        <Segment style={{ margin: 0, border: "none", boxShadow: "none", padding: 0 }}>
          <Dimmer.Dimmable dimmed={loading}>
            <Dimmer active={loading} inverted>
              <Loader>Loading</Loader>
            </Dimmer>
            <DataTable
              className="documents-table"
              columns={tableColumns}
              rows={sortedEntries}
              getRowKey={(row) => row.acknowledgementId || `${row.userId}-${row.documentId}`}
              renderRow={(row) => (
                <>
                  <td>
                    <div className="documents-compliance-user">
                      {row.imageUrl ? (
                        <img className="documents-compliance-avatar" src={row.imageUrl} alt="" loading="lazy" />
                      ) : (
                        <div className="documents-compliance-avatar documents-compliance-avatar--placeholder" aria-hidden>
                          {(row.userName || "?").trim().charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="documents-compliance-user-text">
                        <div className="documents-compliance-user-name">{row.userName}</div>
                        <div className="documents-compliance-user-meta">
                          {[row.email, row.designation].filter(Boolean).join(" · ") || "—"}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>{row.documentTitle}</td>
                  <td>{getCategoryLabel(row.documentCategory)}</td>
                  <td>{row.departmentName}</td>
                  <td>{row.officeName}</td>
                  <td>
                    <span className={`documents-chip ${statusChipClass(row.status)}`}>{statusLabel(row.status)}</span>
                  </td>
                  <td>{row.status === ACCEPTANCE_STATUS.Pending ? "—" : formatRespondedDate(row.respondedDate)}</td>
                  <td className="documents-table-td-actions">
                    {hasRecordedResponse(row.status) ? (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary documents-btn documents-compliance-response-btn"
                        onClick={() => setResponseDetailRow(row)}
                      >
                        <i className="bi bi-chat-left-text me-1" aria-hidden />
                        View response
                      </button>
                    ) : (
                      <span className="documents-compliance-response-placeholder">—</span>
                    )}
                  </td>
                </>
              )}
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={handleSortColumn}
              loading={false}
              emptyMessage="No acknowledgement rows match the current filters."
              stateCellStyle={{ padding: "1.5rem", textAlign: "center", color: "#6b7280" }}
            />
            <div
              className={`documents-pagination documents-pagination--grid-pager${loading ? " is-loading" : ""}`}
              aria-busy={loading}
            >
              <span className="documents-pagination-total">
                {totalCount === 0
                  ? "No results"
                  : `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, totalCount)} of ${totalCount}`}
              </span>
              <div className="documents-pagination-pager-wrap">
                <GridPager
                  className="documents-rc-pagination"
                  onChange={handlePagerChange}
                  current={page}
                  total={totalCount}
                  pageSize={pageSize}
                  defaultPageSize={20}
                  showLessItems
                  showTitle={false}
                  showSizeChanger={false}
                />
              </div>
            </div>
          </Dimmer.Dimmable>
        </Segment>
      </div>

      {responseDetailRow ? (
        <div
          className="documents-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="compliance-response-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) setResponseDetailRow(null);
          }}
        >
          <div
            className="documents-modal documents-modal--upload-single documents-modal--compliance-response"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="documents-modal-preview"
              style={{ borderRight: "none", background: "#fff" }}
            >
              <div className="documents-upload-modal-header">
                <div>
                  <p className="documents-upload-modal-eyebrow">Acknowledgement</p>
                  <h2 className="documents-upload-modal-title" id="compliance-response-title">
                    Response detail
                  </h2>
                </div>
                <button
                  type="button"
                  className="btn btn-light documents-btn"
                  aria-label="Close response detail"
                  onClick={() => setResponseDetailRow(null)}
                >
                  Close
                </button>
              </div>

              <div className="documents-modal-sheet documents-modal-sheet--upload documents-compliance-response-sheet">
                <div className="documents-compliance-response-meta">
                  <div className="documents-compliance-response-meta-block">
                    <span className="documents-compliance-response-meta-label">Employee</span>
                    <span className="documents-compliance-response-meta-value">{responseDetailRow.userName}</span>
                  </div>
                  <div className="documents-compliance-response-meta-block">
                    <span className="documents-compliance-response-meta-label">Document</span>
                    <span className="documents-compliance-response-meta-value">{responseDetailRow.documentTitle}</span>
                  </div>
                  <div className="documents-compliance-response-meta-row">
                    <div className="documents-compliance-response-meta-block">
                      <span className="documents-compliance-response-meta-label">Status</span>
                      <span>
                        <span className={`documents-chip ${statusChipClass(responseDetailRow.status)}`}>
                          {statusLabel(responseDetailRow.status)}
                        </span>
                      </span>
                    </div>
                    <div className="documents-compliance-response-meta-block">
                      <span className="documents-compliance-response-meta-label">Response date</span>
                      <span className="documents-compliance-response-meta-value">
                        {formatRespondedDate(responseDetailRow.respondedDate)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="documents-compliance-response-comment-section">
                  <span className="documents-compliance-response-meta-label">Comment</span>
                  <div className="documents-compliance-response-comment-box">
                    {String(responseDetailRow.responseComment || "").trim() !== "" ? (
                      <p className="documents-compliance-response-comment-text">
                        {String(responseDetailRow.responseComment).trim()}
                      </p>
                    ) : (
                      <p className="documents-compliance-response-comment-empty">No comment was provided.</p>
                    )}
                  </div>
                </div>

                <div className="documents-modal-actions documents-compliance-response-actions">
                  <button
                    type="button"
                    className="btn btn-primary documents-btn"
                    onClick={() => setResponseDetailRow(null)}
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ComplianceTracking;
